import { __t } from '../i18n/t'

// Credencial de conexão (parceiro e posto auto-system). O backend exige as 5 chaves quando
// o objeto vem preenchido e descarta quando vem todo vazio.
const isBlank = (v: unknown) => v === null || v === undefined || v === ''

export const isEmptyCredentials = (credentials?: Record<string, unknown> | null) =>
  !credentials || Object.values(credentials).every(isBlank)

/**
 * O NumberInput do react-admin nasce null e os TextInput ficam undefined, então uma aba
 * de credenciais tocada sem preencher virava `{"port":null}` e o backend devolvia 400.
 * Credencial toda vazia é "não informada": sai do payload. `enabled=false` apaga sempre
 * (posto cujo sistema de sincronização não usa credencial).
 */
export const normalizeCredentials = <T extends { credentials?: Record<string, unknown> | null }>(
  data: T,
  enabled = true,
): T => {
  if (!enabled || isEmptyCredentials(data.credentials)) {
    delete data.credentials
  }
  return data
}

/** Linha vazia do iterator manda `numericId: null`; o backend exige número em cada concentrador. */
export const filterConcentrators = <T extends { concentrators?: Array<{ numericId?: unknown } | null> }>(
  data: T,
): T => {
  if (Array.isArray(data.concentrators)) {
    data.concentrators = data.concentrators.filter(c => !!c && !isBlank(c.numericId))
  }
  return data
}

/** Linha vazia do iterator de "IDs Numéricos" manda `null`; o Joi (JoiArray de number) recusa. */
export const filterNumericIds = <T extends { numericIds?: unknown[] | null }>(data: T): T => {
  if (Array.isArray(data.numericIds)) {
    data.numericIds = data.numericIds.filter(n => !isBlank(n))
  }
  return data
}

// Validators (formato do react-admin: (valor, todosOsValores) => mensagem | undefined).
// Funções, não constantes de módulo com texto: a mensagem é traduzida na hora da validação.
export const requiredWithCredentials = (value: unknown, allValues: { credentials?: Record<string, unknown> | null }) =>
  isBlank(value) && !isEmptyCredentials(allValues?.credentials)
    ? __t('Preencha todas as credenciais ou deixe todas em branco')
    : undefined

// O backend aceita hostname ou IP: URL com http:// ou host:porta dava 400 sem dizer o campo.
export const validateCredentialHost = (value: unknown) => {
  if (isBlank(value) || typeof value !== 'string') return undefined
  const hasPort = (value.match(/:/g) || []).length === 1
  return value.includes('/') || hasPort
    ? __t('Informe só o host ou IP, sem http:// e sem porta')
    : undefined
}
